/**
 * CDL Raw Data CSV Export
 *
 * Saves raw CDL sensor data as CSV files to the local dev server filesystem.
 * Files are saved to /cdl_csv_export/ in the project root.
 * Gated behind localStorage flag: localStorage.setItem("cdl_csv_export", "true")
 *
 * In dev mode, files are POSTed to the Vite dev server plugin at /api/save-csv.
 * In production, falls back to browser download.
 */

/** Check if CSV export is enabled */
export function isCsvExportEnabled(): boolean {
  try {
    return localStorage.getItem("cdl_csv_export") === "true";
  } catch {
    return false;
  }
}

/** Save a CSV file — tries dev server first, falls back to browser download */
async function saveCsv(filename: string, csvContent: string): Promise<void> {
  // Try dev server endpoint first
  try {
    const resp = await fetch("/api/save-csv", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ filename, content: csvContent }),
    });
    if (resp.ok) {
      const result = await resp.json();
      console.log(`[CSV Export] Saved to server: ${result.path}`);
      return;
    }
  } catch {
    // Dev server not available — fall back to browser download
  }

  // Fallback: browser download
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
  console.log(`[CSV Export] Downloaded via browser: ${filename}`);
}

/**
 * Convert an array of {xValue/dt, yValue/value} data points to a Map<hour, value>
 * for alignment with other signals.
 */
function toTimestampValuePairs(
  data: Array<{
    xValue?: any;
    yValue?: any;
    dt?: any;
    value?: any;
    quant2?: any;
  }>
): Array<{ timestamp: number; value: number }> {
  return data
    .map((p) => {
      const raw = p.xValue ?? p.dt;
      const ts =
        typeof raw === "number"
          ? raw
          : new Date(String(raw).replace(" ", "T")).getTime();
      // Support multiple value field names: yValue (HR/RR), value (motion/bedExit), quant2 (illumination)
      const val = Number(p.yValue ?? p.value ?? p.quant2) || 0;
      return { timestamp: ts, value: val };
    })
    .filter((p) => !isNaN(p.timestamp) && p.timestamp > 0)
    .sort((a, b) => a.timestamp - b.timestamp);
}

/** Convert hour-indexed data (dt=0-23) to epoch timestamps for a given date */
function convertHourIndexToTimestamps(
  data: Array<{ dt: any; value: any }>,
  dateStr: string
): Array<{ dt: number; value: any }> {
  const baseDate = new Date(dateStr + "T00:00:00");
  return data.map((p) => {
    const hour = Number(p.dt);
    if (!isNaN(hour) && hour >= 0 && hour <= 23) {
      const d = new Date(baseDate);
      d.setHours(hour, 0, 0, 0);
      return { dt: d.getTime(), value: p.value };
    }
    return p;
  });
}

interface BackfillSignals {
  bedExitData: Array<{ dt: any; value: any }>;
  motionData: Array<{ dt: any; value: any }>;
  noiseMeanData: Array<{ dt: any; value: any }>;
  hrData: Array<{ xValue: any; yValue: any }>;
  rrData: Array<{ xValue: any; yValue: any }>;
  illuminationData?: Array<{ dt: any; value: any }>;
}

/**
 * Export backfill pipeline data as a CSV download.
 * Called from sleepQualityBackfill.ts after fetching all signals for a day.
 *
 * Creates a single CSV with all signals aligned by timestamp.
 */
export function downloadBackfillCsv(
  patientId: number,
  date: string,
  signals: BackfillSignals
): void {
  if (!isCsvExportEnabled()) return;

  // Motion and bed_exit data have dt as hour indices (0-23), convert to epoch timestamps
  const bedExitWithTs = convertHourIndexToTimestamps(signals.bedExitData, date);
  const motionWithTs = convertHourIndexToTimestamps(signals.motionData, date);

  // Collect all unique timestamps across all signals
  const allPairs: Record<
    string,
    Array<{ timestamp: number; value: number }>
  > = {
    bed_exit: toTimestampValuePairs(bedExitWithTs),
    motion: toTimestampValuePairs(motionWithTs),
    noise_mean: toTimestampValuePairs(signals.noiseMeanData),
    hr: toTimestampValuePairs(signals.hrData),
    rr: toTimestampValuePairs(signals.rrData),
  };
  if (signals.illuminationData) {
    allPairs.illumination = toTimestampValuePairs(signals.illuminationData);
  }

  // Build a master timestamp set
  const tsSet = new Set<number>();
  for (const pairs of Object.values(allPairs)) {
    for (const p of pairs) tsSet.add(p.timestamp);
  }

  if (tsSet.size === 0) {
    console.log(`[CSV Export] ${date}: No data points to export`);
    return;
  }

  const sortedTimestamps = Array.from(tsSet).sort((a, b) => a - b);

  // Build lookup maps for each signal (timestamp → value)
  const lookups: Record<string, Map<number, number>> = {};
  for (const [key, pairs] of Object.entries(allPairs)) {
    const map = new Map<number, number>();
    for (const p of pairs) map.set(p.timestamp, p.value);
    lookups[key] = map;
  }

  const signalKeys = Object.keys(allPairs);
  const header = ["timestamp", "datetime", ...signalKeys].join(",");

  const rows = sortedTimestamps.map((ts) => {
    const dt = new Date(ts).toISOString();
    const values = signalKeys.map((key) => lookups[key].get(ts) ?? "");
    return [ts, dt, ...values].join(",");
  });

  const csv = [header, ...rows].join("\n");
  const filename = `cdl_${patientId}_${date}.csv`;

  console.log(
    `[CSV Export] Saving ${filename} (${sortedTimestamps.length} rows, ${signalKeys.length} signals)`
  );
  saveCsv(filename, csv);
}

/**
 * Export raw CDL API response data (from getSleepAnalysisData) as CSV.
 * Works with the processed data after WASM decoding (arr arrays are populated).
 */
export function downloadRawCdlCsv(
  patientId: number,
  date: string,
  processed3s: Record<
    string,
    Record<
      string,
      { arr: Array<string | number> | Float64Array | null; type: string }
    >
  >,
  processedHR: Record<
    string,
    Record<
      string,
      { arr: Array<string | number> | Float64Array | null; type: string }
    >
  >
): void {
  if (!isCsvExportEnabled()) return;

  // Export 3s database signals
  exportProcessedDbCsv(patientId, date, "3s", processed3s, "timestamp_rr");

  // Export HR3s database signals
  exportProcessedDbCsv(patientId, date, "HR3s", processedHR, "timestamp_hr");
}

function exportProcessedDbCsv(
  patientId: number,
  date: string,
  dbName: string,
  processed: Record<
    string,
    Record<
      string,
      { arr: Array<string | number> | Float64Array | null; type: string }
    >
  >,
  timestampKey: string
): void {
  // Find the field group that contains our data (e.g., "rr_3s.ds_min")
  const fieldGroups = Object.keys(processed).filter(
    (k) => k !== "idx" && typeof processed[k] === "object"
  );

  if (fieldGroups.length === 0) return;

  // Extract timestamps and signal names
  let timestamps: Array<string | number> = [];
  const signalData: Record<string, Array<string | number>> = {};

  for (const group of fieldGroups) {
    const fields = processed[group];
    if (!fields || typeof fields !== "object") continue;

    for (const [fieldName, fieldValue] of Object.entries(fields)) {
      if (!fieldValue?.arr) continue;
      const arr = Array.from(fieldValue.arr);

      if (fieldName === timestampKey && arr.length > timestamps.length) {
        timestamps = arr;
      } else if (fieldName !== timestampKey && fieldName !== "count") {
        // Use group.field as column name (e.g., "rr_3s.ds_min.rr_3s")
        const colName = `${group}.${fieldName}`;
        signalData[colName] = arr;
      }
    }
  }

  if (timestamps.length === 0) {
    console.log(`[CSV Export] ${date} ${dbName}: No timestamps found`);
    return;
  }

  const signalKeys = Object.keys(signalData);
  const header = ["timestamp", "datetime", ...signalKeys].join(",");

  const rows = timestamps.map((ts, i) => {
    const tsNum = Number(ts);
    const dt = !isNaN(tsNum) && tsNum > 0 ? new Date(tsNum).toISOString() : "";
    const values = signalKeys.map((key) => {
      const arr = signalData[key];
      return i < arr.length ? arr[i] : "";
    });
    return [ts, dt, ...values].join(",");
  });

  const csv = [header, ...rows].join("\n");
  const filename = `cdl_${patientId}_${date}_${dbName}.csv`;

  console.log(
    `[CSV Export] Saving ${filename} (${timestamps.length} rows, ${signalKeys.length} signals)`
  );
  saveCsv(filename, csv);
}
